import { twitchService } from './lib/twitch';
import { TabManager } from './components/TabManager';
import { ChatComponent } from './components/ChatComponent';
import { ViewersComponent } from './components/ViewersComponent';
import { OnlineComponent } from './components/OnlineComponent';
import { RaidQueueComponent } from './components/RaidQueueComponent';
import { FloatingRaidButton } from './components/FloatingRaidButton';
import { EventsComponent } from './components/EventsComponent';
import { TeammatesComponent } from './components/TeammatesComponent';
import { queueManager } from './services/QueueManager';
import { channelDetailsModal } from './components/ChannelDetailsModal';
import { helpModal } from './components/HelpModal';
import TwitchEventSubService from "./services/TwitchEventSubService";
import { initialTokenValidation } from './services/InitialTokenValidation';
import { networkMonitor } from './services/NetworkMonitor';
import './styles/variables.css';
import './style.css';

export async function createMainApp(): Promise<HTMLElement> {
  // Initialize network monitoring
  networkMonitor; // This will create the singleton instance

  // Run initial token validation
  await initialTokenValidation.validateInitialToken();

  const user = twitchService.getUser();
  if (!user) {
    window.location.href = '/login';
    return document.createElement('div');
  }

  const container = document.createElement('div');
  container.innerHTML = `
    <header class="header">
      <div class="header-top">
        <div class="header-content">
          <div class="logo-section">
            <span class="logo-image">
              <img src="/assets/images/firepanel.png"/></span>
            <span class="logo-text">FirePanel</span>
          </div>
          <div class="profile-section">
            <button class="help-button" title="Help">
              <span class="help-icon material-symbols-outlined">help</span>
            </button>
            <button class="teammates-button" title="Online Teammates">
              <span class="teammates-icon material-symbols-outlined">group</span>
              <span class="teammates-badge">0</span>
            </button>
            <button class="raid-queue-button" title="Raid Queue">
              <span class="raid-queue-icon material-symbols-outlined">dynamic_form</span>
              <span class="raid-queue-badge">0</span>
            </button>
            <img src="${user.profile_image_url}" alt="${user.display_name}" class="profile-image" title="${user.display_name}">
            <button class="logout-button" title="Logout">
              <span class="logout-icon">⏻</span>
            </button>
          </div>
        </div>
      </div>
      <div class="header-tabs">
        <div class="header-content">
          <div class="tabs">
            <button class="tab-button active" data-tab="chat">Chat</button>
            <button class="tab-button" data-tab="viewers">Viewers</button>
            <button class="tab-button" data-tab="online">Who's Online</button>
            <button class="tab-button" data-tab="events">Events</button>
          </div>
        </div>
      </div>
    </header>

    <div id="chat" class="tab-content active">
      <div class="chat-container">
        <div class="chat-messages" id="chatMessages">
          <div class="empty-chat">
            <span class="empty-chat-icon">💭</span>
            <span class="empty-chat-text">No chat messages</span>
          </div>
        </div>
      </div>
    </div>

    <div id="viewers" class="tab-content">
      <div class="viewers-container">
        <div class="viewers-header">
          <div class="viewers-metrics">
            <div class="viewer-metric">
              <span class="viewer-metric-label">Total Viewers:</span>
              <span class="viewer-metric-value" id="totalViewers">0</span>
            </div>
            <div class="viewer-metric">
              <span class="viewer-metric-label">Real Viewers:</span>
              <span class="viewer-metric-value" id="realViewers">0</span>
            </div>
            <div class="viewer-metric">
              <span class="viewer-metric-label">Known Bots:</span>
              <span class="viewer-metric-value" id="knownBots">0</span>
            </div>
          </div>
          <div class="viewers-controls">
            <div class="search-row">
              <input type="text" class="viewers-search" placeholder="Search viewers...">
              <button class="refresh-button" title="Refresh">🔄</button>
            </div>
            <div class="filter-row">
              <div class="filter-buttons">
                <button class="filter-button active" data-filter="all">All</button>
                <button class="filter-button" data-filter="moderator">Mods</button>
                <button class="filter-button" data-filter="vip">VIPs</button>
                <button class="filter-button" data-filter="bot">Bots</button>
                <button class="filter-button" data-filter="viewer">Viewers</button>
              </div>
            </div>
            <div class="sort-row">
              <select class="viewers-sort">
                <option value="username">Sort by Username</option>
              </select>
            </div>
          </div>
        </div>
        <div class="viewers-list">
          <!-- Viewer items will be dynamically added here -->
        </div>
      </div>
    </div>

    <div id="online" class="tab-content">
      <div class="online-container">
        <div class="streamer-list"></div>
      </div>
    </div>

    <div id="events" class="tab-content"></div>

    <div class="chat-input-container" id="chat-input">
      <div class="metrics-row">
        <div class="metric" title="Total Viewers">
          <span class="material-symbols-outlined">group</span>
          <span class="metric-value" id="chatTotalViewers">0</span>
        </div>
        <div class="metric" title="Real Viewers">
          <span class="material-symbols-outlined">person</span>
          <span class="metric-value" id="chatRealViewers">0</span>
        </div>
        <div class="metric" title="Known Bots">
          <span class="material-symbols-outlined">smart_toy</span>
          <span class="metric-value" id="chatKnownBots">0</span>
        </div>
      </div>
      <div class="chat-input-content">
        <div class="chat-input" contenteditable="true" id="chatInput" placeholder="Type your message..."></div>
        <button class="emote-button" title="Add Emote">😀</button>
        <button class="send-button" id="sendButton">Send</button>
        <div class="emote-picker">
          <div class="emote-picker-header">
            <div class="emote-picker-title">
              <span>Emotes</span>
              <button class="emote-picker-close" title="Close">×</button>
            </div>
            <input type="text" class="emote-search" placeholder="Search emotes...">
          </div>
          <div class="emote-picker-content"></div>
        </div>
      </div>
    </div>
  `;

  // Create raid queue popout
  const raidQueuePopout = document.createElement('div');
  raidQueuePopout.className = 'raid-queue-popout';
  raidQueuePopout.innerHTML = `
    <div class="raid-queue-popout-header">
      <div class="raid-queue-popout-title">Raid Queue</div>
      <button class="raid-queue-popout-close">×</button>
    </div>
    <div class="raid-queue-popout-content">
      <div class="raid-queue-list"></div>
    </div>
  `;
  document.body.appendChild(raidQueuePopout);

  // Create teammates popout
  const teammatesPopout = document.createElement('div');
  teammatesPopout.className = 'teammates-popout';
  teammatesPopout.innerHTML = `
    <div class="teammates-popout-header">
      <div class="teammates-popout-title">Online Teammates</div>
      <button class="teammates-popout-close">×</button>
    </div>
    <div class="teammates-popout-subtitle">
      <span class="teammates-source">via R3dLabs Teams</span>
    </div>
    <div class="teammates-popout-content">
      <div class="teammates-list"></div>
    </div>
  `;
  document.body.appendChild(teammatesPopout);

  // Initialize components
  const chatComponent = new ChatComponent(container);
  const viewersComponent = new ViewersComponent(container);
  const onlineComponent = new OnlineComponent(container);
  const raidQueueComponent = new RaidQueueComponent(container);
  const eventsComponent = new EventsComponent();
  
  const twitchEventSubService = new TwitchEventSubService();
  twitchEventSubService.connect()

  // Create tab initializers map
  const tabInitializers = new Map([
    ['chat', () => chatComponent.initialize()],
    ['viewers', () => viewersComponent.initialize()],
    ['online', () => onlineComponent.initialize()],
    ['events', async () => {
      const eventsTab = document.getElementById('events');
      if (eventsTab) {
        eventsTab.appendChild(eventsComponent.getElement());
        await eventsComponent.initializeEvents();
      }
      return () => eventsComponent.destroy();
    }]
  ]);

  // Initialize tab manager
  const tabManager = new TabManager(container, tabInitializers);

  // Add click handler to logo
  const logoSection = container.querySelector('.logo-section') as HTMLDivElement;
  if (logoSection) {
    logoSection.style.cursor = 'pointer';
    logoSection.addEventListener('click', () => {
      tabManager.switchTab('chat');
    });
  }

  // Initialize services
  await raidQueueComponent.initialize();
  
  new FloatingRaidButton();
  queueManager.start();
  
  // Initialize modals (this makes them available via console)
  channelDetailsModal;
  helpModal;

  // Add help button handler
  const helpButton = container.querySelector('.help-button');
  if (helpButton) {
    helpButton.addEventListener('click', () => {
      helpModal.show();
    });
  }

  // Add logout handler
  const logoutButton = container.querySelector('.logout-button');
  if (logoutButton) {
    logoutButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (confirm('Are you sure you want to log out?')) {
        twitchService.logout();
        window.location.href = '/';
      }
    });

    // Add tooltip behavior
    logoutButton.addEventListener('mouseenter', () => {
      logoutButton.setAttribute('title', `Log out ${user.display_name}`);
    });
  }

  // Simple OBS refresh logic
  const RELOAD_AFTER_HIDDEN_MINUTES = 30; // Reload if hidden for more than 30 minutes
  
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const lastHiddenTime = localStorage.getItem('lastHiddenTime');
      if (lastHiddenTime) {
        const minutesHidden = (Date.now() - parseInt(lastHiddenTime, 10)) / 60000;
        if (minutesHidden > RELOAD_AFTER_HIDDEN_MINUTES) {
          console.log(`Page was hidden for ${Math.round(minutesHidden)} minutes, reloading`);
          window.location.reload();
          return;
        }
        
        // If hidden for more than 5 minutes, check chat connection health
        if (minutesHidden > 5) {
          console.log(`Page was hidden for ${Math.round(minutesHidden)} minutes, checking chat connection health`);
          ChatComponent.checkChatHealth();
        }
      }
    } else {
      // Page is being hidden, store the timestamp
      localStorage.setItem('lastHiddenTime', Date.now().toString());
    }
  });

  // Initialize teammates component at the very end, after container is fully set up
  // Create teammates component AFTER popout is in DOM
  const teammatesComponent = new TeammatesComponent();
  
  // Defer teammates component initialization until DOM is ready
  setTimeout(async () => {
    try {
      await teammatesComponent.initialize();
    } catch (error) {
      console.error('App: Failed to initialize teammates component:', error);
      // Don't let teammates component failure break the app
    }
  }, 100); // Small delay to ensure DOM is ready

  return container;
}
